import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@stackje/shared-components/ui/button";
import {
  ClearableInput,
  ClearableTextarea,
} from "@stackje/shared-components/ui/clearable-fields";
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@stackje/shared-components/ui/dialog";
import { Label } from "@stackje/shared-components/ui/label";
import type { SelectedImageData } from "@/components/shared/ImageEditor";
import { FormImageUploader } from "@/components/shared/FormImageUploader";
import { getFallbackLinkText } from "@stackje/shared-components/lib/link-utils";
import { UnsavedChangesConfirmDialog } from "@/components/shared/UnsavedChangesConfirmDialog";

// Zod schema for link validation
const linkSchema = z.object({
  title: z.string().optional(),
  url: z.string().regex(/^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/, "Please enter a valid URL (e.g., https://example.com)"),
  description: z.string().optional(),
  image: z.object({
    src: z.string(),
    alt: z.string().optional(),
  }).optional(),
});

type LinkFormData = z.infer<typeof linkSchema>;

interface LinkEditFormDialogContentProps {
  isEditing: boolean;
  initialData?: LinkFormData;
  onSubmit: (data: LinkFormData) => void;
  onClose?: () => void;
  editingLinkIndex?: number | null; // Optional, for unique name/id generation
  linksLength?: number; // Optional, for unique name/id generation
}

export function LinkEditFormDialogContent({
  isEditing,
  initialData,
  onSubmit,
  onClose,
  editingLinkIndex,
  linksLength,
}: LinkEditFormDialogContentProps) {
  const form = useForm<LinkFormData>({
    resolver: zodResolver(linkSchema),
    defaultValues: {
      title: initialData?.title || "",
      url: initialData?.url || "",
      description: initialData?.description || "",
    },
    mode: "onChange",
  });

  // Local state for image since it's not a simple form field
  const [imageValue, setImageValue] = useState<SelectedImageData | null>(
    initialData?.image ? { fullUrl: initialData.image.src, previewUrl: initialData.image.src } : null
  );

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const isDirty = form.formState.isDirty || 
    (imageValue?.fullUrl !== initialData?.image?.src && imageValue?.fullUrl !== null);

  const handleSubmit = form.handleSubmit((data) => {
    // Transform image data if needed
    const submitData: LinkFormData = {
      ...data,
      image: imageValue?.fullUrl ? {
        src: imageValue.fullUrl,
        alt: data.title || getFallbackLinkText({ url: data.url, title: data.title }),
      } : undefined,
    };
    onSubmit(submitData);
    form.reset();
  });

  // Reset form when editing different link (initialData changes) or when switching between add/edit modes
  useEffect(() => {
    if (initialData) {
      form.reset({
        title: initialData.title || "",
        url: initialData.url || "",
        description: initialData.description || "",
      });
      // Also update the image preview
      setImageValue(
        initialData.image ? { fullUrl: initialData.image.src, previewUrl: initialData.image.src } : null
      );
    } else {
      // When adding new link, reset form
      form.reset({
        title: "",
        url: "",
        description: "",
      });
      setImageValue(null);
    }
  }, [initialData, isEditing, form]);

  const handleClose = () => {
    if (isDirty) {
      setShowConfirmDialog(true);
    } else {
      onClose?.();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onClose?.();
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <DialogContent
        className="w-full h-[100dvh] sm:max-w-[425px] sm:w-[90vw] sm:h-auto sm:max-h-[85vh] overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        onClose={handleClose}
      >
      <DialogHeader className="flex-shrink-0">
        <DialogTitle>{isEditing ? "Edit link" : "Add a link"}</DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-4 p-4 flex-1 overflow-y-auto min-w-0">
        <div className="flex flex-col gap-2 min-w-0">
          <div className="flex flex-col mb-2">
            <p className="text-sm text-muted-foreground">
              Not all fields will be used for a given card type.
            </p>
            <p className="text-sm text-muted-foreground">
              <span className="text-red-500">*</span> indicates required fields
            </p>
          </div>
          <Label htmlFor="linkTitleDialog">Title</Label>
          <Controller
            name="title"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="linkTitleDialog"
                name={`linkTitleDialog${isEditing ? editingLinkIndex : linksLength}`}
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Enter link title (domain will be used if empty)"
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="linkUrlDialog">
            URL <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="url"
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <ClearableInput
                  id="linkUrlDialog"
                  name={`linkUrlDialog${isEditing ? editingLinkIndex : linksLength}`}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Enter link URL"
                />
                {fieldState.error && (
                  <p className="text-sm text-red-500">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="linkDescriptionDialog">Description</Label>
          <Controller
            name="description"
            control={form.control}
            render={({ field }) => (
              <ClearableTextarea
                id="linkDescriptionDialog"
                name={`linkDescriptionDialog${isEditing ? editingLinkIndex : linksLength}`}
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Enter link description"
                rows={2}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          <FormImageUploader
            label="Link Image"
            currentImage={imageValue}
            onImageChange={setImageValue}
            allowFreeCrop={true}
          />
        </div>
      </div>
      <DialogFooter className="flex-shrink-0 sticky bottom-0 bg-background border-t pt-4 mt-auto">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={form.formState.isSubmitting || !form.formState.isValid}
          className="w-full"
        >
          {isEditing ? "Save changes" : "Add link"}
        </Button>
      </DialogFooter>
    </DialogContent>

    <UnsavedChangesConfirmDialog
      open={showConfirmDialog}
      onOpenChange={setShowConfirmDialog}
      onConfirm={handleConfirmClose}
      onCancel={handleCancelClose}
    />
    </>
  );
} 